
import { 
  doc, 
  getDoc, 
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../config";
import { addTransaction } from "../transactions";

export const approveTransfer = async (transferId: string, approverId: string) => {
  try {
    // Get the transfer
    const transferRef = doc(db, "transfers", transferId);
    const transferDoc = await getDoc(transferRef);
    
    if (!transferDoc.exists()) {
      throw new Error("Transfer not found");
    }
    
    const transferData = transferDoc.data();
    
    if (transferData.status !== 'pending') {
      throw new Error("Transfer is not pending");
    }
    
    // Get both users to validate balances
    const fromUserRef = doc(db, "users", transferData.fromUserId);
    const toUserRef = doc(db, "users", transferData.toUserId);
    
    const [fromUserDoc, toUserDoc] = await Promise.all([
      getDoc(fromUserRef),
      getDoc(toUserRef)
    ]);
    
    if (!fromUserDoc.exists() || !toUserDoc.exists()) {
      throw new Error("One or both users not found");
    }
    
    const fromUserData = fromUserDoc.data();
    const toUserData = toUserDoc.data();
    
    // Check if sender still has sufficient balance
    if (fromUserData.balance < transferData.amount) {
      throw new Error("Sender no longer has sufficient balance");
    }
    
    // Create descriptions that include the additional description if provided
    const baseFromDescription = `Transfer to ${toUserData.firstName}`;
    const baseToDescription = `Transfer from ${fromUserData.firstName}`;
    
    const fromDescription = transferData.description 
      ? `${baseFromDescription} - ${transferData.description}`
      : baseFromDescription;
    
    const toDescription = transferData.description 
      ? `${baseToDescription} - ${transferData.description}`
      : baseToDescription;
    
    console.log("Creating transfer transactions:", {
      fromUser: transferData.fromUserId,
      toUser: transferData.toUserId,
      amount: transferData.amount,
      fromBalance: fromUserData.balance,
      toBalance: toUserData.balance,
      fromDescription,
      toDescription
    });
    
    await Promise.all([
      // Debit transaction for sender (negative amount)
      addTransaction(transferData.fromUserId, -transferData.amount, fromDescription),
      // Credit transaction for receiver (positive amount)
      addTransaction(transferData.toUserId, transferData.amount, toDescription)
    ]);
    
    // Delete the transfer record instead of updating status
    await deleteDoc(transferRef);
    
    console.log("Transfer approved and deleted successfully");
    return true;
  } catch (error) {
    console.error("Error approving transfer: ", error);
    throw error;
  }
};

export const rejectTransfer = async (transferId: string, approverId: string) => {
  try {
    const transferRef = doc(db, "transfers", transferId);
    const transferDoc = await getDoc(transferRef);
    
    if (!transferDoc.exists()) {
      throw new Error("Transfer not found");
    }
    
    const transferData = transferDoc.data();
    
    if (transferData.status !== 'pending') {
      throw new Error("Transfer is not pending");
    }
    
    // Delete the transfer record instead of updating status
    await deleteDoc(transferRef);
    
    console.log("Transfer rejected and deleted successfully");
    return true;
  } catch (error) {
    console.error("Error rejecting transfer: ", error);
    throw error;
  }
};

export const deleteTransfer = async (transferId: string) => {
  try {
    await deleteDoc(doc(db, "transfers", transferId));
    return true;
  } catch (error) {
    console.error("Error deleting transfer: ", error);
    throw error;
  }
};
